import { ViewContainerRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { combineLatest, concat, Observable, race, Subject } from 'rxjs';
import { filter, map, shareReplay, take, tap, withLatestFrom } from 'rxjs/operators';
import { CUSTOMERS, Customers, getEntities } from '../../store';
import {
  CustomerSelectDialogComponent,
  CustomerSelectDialogData,
  CustomerSelectDialogResponse,
} from '../customer-select/customer-select-dialog.component';
import { CustomerListDto } from '@careapp-group/ng-sdk';
import { CustomerDto } from '@careapp-group/ng-sdk';

export abstract class _CustomerContentCreation {
  protected _customer$: Observable<CustomerDto>;
  protected _customers$: Observable<CustomerDto[]>;

  protected _customerIds$ = new Subject<string[]>();
  protected _customerListIds$ = new Subject<string[]>();

  protected initialCustomer$: Observable<CustomerDto>;

  private _customerId?: string;
  protected get customerId(): string {
    return this._customerId;
  }
  protected set customerId(value: string) {
    this._customerId = value;
  }
  private _customerIds?: string[];
  protected get customerIds(): string[] {
    return this._customerIds;
  }
  protected set customerIds(value: string[]) {
    this._customerIds = value;
  }
  private _customerListIds?: string[];
  protected get customerListIds(): string[] {
    return this._customerListIds;
  }
  protected set customerListIds(value: string[]) {
    this._customerListIds = value;
  }

  abstract get options(): {
    multi: boolean;
    hideUnassigned?: boolean;
    disableNoMedia?: boolean;
    disableWhereFamilyRole?: boolean;
  };

  constructor(
    protected dialog: MatDialog,
    protected store: Store<any>,
    protected viewContainerRef: ViewContainerRef,
    initialId: string
  ) {
    if (initialId) {
      this._customerIds$.next([initialId]);
    }

    this.initialCustomer$ = this.store.pipe(
      select(getEntities(CUSTOMERS)),
      map((customers) => customers[initialId]),
      tap((customer) => {
        if (initialId && !customer) {
          this.store.dispatch(Customers.RequestByIdAction({ id: initialId }));
        }
      }),
      filter((customer) => !!customer),
      take(1),
      // Important as we do not one to dispatch an action per subscription
      shareReplay()
    );

    this._customers$ = this._customerIds$.pipe(
      withLatestFrom(this.store.select(getEntities(CUSTOMERS))),
      map(([ids, customers]) => ids.map((id) => customers[id]))
    );
    this._customer$ = this._customers$.pipe(map((customers) => customers[0]));
  }

  async openCustomerSelection(initialTab?: 'all' | 'my' | 'lists') {
    const dialogRef = this.dialog.open(CustomerSelectDialogComponent, {
      width: '60%',
      maxHeight: '90vh',
      // Ensures change detection works as expected
      viewContainerRef: this.viewContainerRef,
      data: <CustomerSelectDialogData>{
        ...this.options,
        selectedIds: this.options.multi
          ? this.customerIds
          : this.customerId
          ? [this.customerId]
          : [],
        selectedCustomerListIds: this.options.multi ? [...(this.customerListIds ?? [])] : [],
        initialTab,
      },
    });
    const selection: CustomerSelectDialogResponse = await dialogRef.afterClosed().toPromise();
    if (!selection || !selection.selectedIds) {
      return;
    }
    if (this.options.multi) {
      this.selectCustomers(selection.selectedIds);
      this.selectCustomerLists(selection.selectedCustomerListIds);
    } else {
      this.selectCustomer(selection.selectedIds[0] || null);
    }
  }

  public setInitialCustomers(customerIds: string[]) {
    this.selectCustomers(customerIds);
  }

  selectCustomers(customerIds: string[]) {
    this._customerIds$.next(customerIds);
    this.customerIds = customerIds;
  }

  selectCustomerLists(customerListIds: string[]) {
    this._customerListIds$.next(customerListIds);
    this.customerListIds = customerListIds;
  }

  selectCustomer(customerId: string) {
    this._customerIds$.next(customerId ? [customerId] : []);
    this.customerId = customerId ? customerId : null;
  }
}

export abstract class SingleCustomerContentCreation extends _CustomerContentCreation {
  abstract get customerId(): string;
  abstract set customerId(id: string);
  public customer$: Observable<CustomerDto>;

  get options() {
    return { multi: false };
  }

  constructor(
    protected dialog: MatDialog,
    protected store: Store<any>,
    protected viewContainerRef: ViewContainerRef,
    initialId?: string
  ) {
    super(dialog, store, viewContainerRef, initialId);
    this.customer$ = race(concat(this.initialCustomer$, this._customer$), this._customer$).pipe(
      // Important as we do not one to dispatch an action per subscription
      shareReplay()
    );
  }
}

export abstract class MultiCustomerContentCreation extends _CustomerContentCreation {
  abstract get customerIds(): string[];
  abstract set customerIds(ids: string[]);
  abstract get customerListIds(): string[];
  abstract set customerListIds(ids: string[]);
  public customers$: Observable<CustomerDto[]>;
  public customerLists$: Observable<CustomerListDto[]>;

  get customerId() {
    return this.customerIds[0];
  }

  get options() {
    return { multi: true };
  }

  constructor(
    protected dialog: MatDialog,
    protected store: Store<any>,
    protected viewContainerRef: ViewContainerRef,
    initialId?: string
  ) {
    super(dialog, store, viewContainerRef, initialId);

    this.customers$ = combineLatest([
      this._customerIds$,
      this.store.select(getEntities(CUSTOMERS)),
    ]).pipe(
      map(([ids, customersMap]) =>
        ids.map((id) => customersMap[id]).filter((customer) => !!customer)
      ),
      shareReplay(1)
    );
  }
}
